import React, { useMemo } from 'react';
import { SentimentAnalysisBadgeWrapper } from './style';
import { SentimentAnalysisResult } from '../../types/enums/sentimentAnalysisResult';
import { colors } from 'crono-fe-common/theme';
import IconNegativeFeedback from 'crono-fe-common/icons/Icon-Negative-Feedback';
import IconPositiveFeedback from 'crono-fe-common/icons/Icon-Positive-Feedback';
import IconNeutralFeedback from 'crono-fe-common/icons/Icon-Neutral-Feedback';
import { Tooltip, Typography } from '@mui/material';
import cn from 'clsx';

export const SENTIMENT_MESSAGES = {
  [SentimentAnalysisResult.POSITIVE]: {
    bgColor: colors.primaryLight,
    color: colors.green,
    icon: <IconPositiveFeedback />,
    label: 'Positive',
    tooltip:
      'The contact expresses interest, enthusiasm or approval, indicating a potential for further engagement.',
  },
  [SentimentAnalysisResult.NEGATIVE]: {
    bgColor: colors.inactiveLight,
    color: colors.inactiveHover,
    icon: <IconNegativeFeedback />,
    label: 'Negative',
    tooltip:
      'The contact expresses disinterest, dissatisfaction or objection, suggesting little to no potential for engagement.',
  },
  [SentimentAnalysisResult.NEUTRAL]: {
    bgColor: colors.nurtureLight,
    color: colors.darkYellow,
    icon: <IconNeutralFeedback />,
    label: 'Neutral',
    tooltip:
      'The contact provides a neutral or indifferent response, showing no clear interest or disinterest.',
  },
  [SentimentAnalysisResult.OUT_OF_OFFICE]: null,
};

interface IProps {
  sentimentAnalysisResult: SentimentAnalysisResult;
  showLabel?: boolean;
  bigVersion?: boolean;
  noBgColor?: boolean;
}

// The component consists in two versions: SentimentAnalysisBadge and getSentimentIcon.
// The first one is used to display the sentiment analysis badge with a label.
// The second one is used to get the sentiment icon only, without the label. (Used on metrics page)
const SentimentAnalysisBadge = ({
  sentimentAnalysisResult,
  showLabel = true,
  bigVersion = false,
  noBgColor = false,
}: IProps) => {
  const element = useMemo(
    () => SENTIMENT_MESSAGES[sentimentAnalysisResult] ?? null,
    [sentimentAnalysisResult],
  );

  if (!element) return null;

  return (
    <Tooltip
      arrow
      enterDelay={400}
      enterNextDelay={400}
      title={
        <span style={{ display: 'flex', textAlign: 'center' }}>
          {element.tooltip}
        </span>
      }
    >
      <SentimentAnalysisBadgeWrapper
        style={{
          backgroundColor: !noBgColor ? element.bgColor : undefined,
          padding: !showLabel ? '2px' : undefined,
        }}
      >
        <div
          className={cn('sentiment-icon', {
            'sentiment-icon--big': bigVersion,
          })}
        >
          {element.icon}
        </div>

        {showLabel && (
          <Typography
            fontSize={bigVersion ? '12px' : '10px'}
            lineHeight={'12px'}
            fontWeight={500}
            color={element.color}
          >
            {element.label}
          </Typography>
        )}
      </SentimentAnalysisBadgeWrapper>
    </Tooltip>
  );
};

export const getSentimentIcon = (
  sentimentAnalysisResult?: SentimentAnalysisResult | null,
) => {
  const element = SENTIMENT_MESSAGES[sentimentAnalysisResult!];

  if (!element) return null;

  return (
    <Tooltip
      arrow
      enterDelay={400}
      enterNextDelay={400}
      title={element.tooltip}
    >
      <div
        style={{
          borderRadius: '50%',
          padding: '2px',
          display: 'flex',
          backgroundColor: element.bgColor,
        }}
      >
        {element.icon}
      </div>
    </Tooltip>
  );
};

export default SentimentAnalysisBadge;
